import React, { useContext, useState } from 'react';
import {
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SwipeableDrawer,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../shared/hooks';
import {
  es_ES,
  en_US,
  pt_BR,
  fr_FR,
  de_DE,
  it_IT,
} from '../../shared/constants/idioms';
import * as translations from './intl';
import PublicIcon from '@mui/icons-material/Public';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Container, NavButton } from './styles';
import { Loading, UserMenu } from '../';
import logo from '../../assets/images/Doctor_Please_Logo.png';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DOMPurify from 'dompurify';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import {
  getCookie,
  setCookie,
  deleteCookie,
} from '../../shared/helpers/cookies';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../shared/constants/globalConstants';

const ChevronDown = styled(ExpandMoreIcon)({
  position: 'absolute',
  color: '#00008f',
  right: '0',
  pointerEvents: 'none',
});

const drawerButtonSx = {
  fontFamily: 'Source Sans Pro !important',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '18px',
  letterSpacing: '1px',
  height: '72px',
  minWidth: '165px',
  color: '#00008F',
  px: '1em',
  justifyContent: 'center',
  alignItems: 'center',
  textTransform: 'uppercase',
  borderBottom: '0.25em solid rgba(0,0,0,0)',
  '&:hover': {
    textDecoration: 'none',
    color: '#00008F',
    backgroundColor: 'rgba(0,0,0,0)',
    borderBottom: '0.25em solid #F07662',
  },
};

const activeSx = {
  borderBottom: '0.25em solid #F07662 !important',
};

const headerButtonSx = {
  height: '64px',
  display: 'inline-grid',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const mobileHeaderButtonSx = {
  height: '48px',
  width: '100%',
  display: 'inline-grid',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'center',
};

const drawerButtonBackSx = {
  color: '#00008F',
  justifyContent: 'left',
  alignItems: 'left',
  fontFamily: 'Source Sans Pro',
  textTransform: 'uppercase',
  lineHeight: '21px',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  '&:hover': {
    textDecoration: 'none',
    color: 'primary.main',
    backgroundColor: 'rgba(0,0,0,0)',
  },
};

const mobileDividerSx = {
  height: '1px',
  width: '100%',
  backgroundColor: '#fafafa',
  margin: '0',
  opacity: 1,
};

const dividerLanguageSx = {
  height: '2em',
  width: '2px',
  backgroundColor: '#fafafa',
  margin: '0',
  opacity: 1,
};

export default function Header() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [isLoading, setIsLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { locale, getIdiom, setIdiom } = useTranslation({
    translationsFile: translations,
  });
  const idiom = getIdiom();
  const { userData } = useContext(GeneralContext);

  const {
    LABEL_PT,
    LABEL_ES,
    LABEL_EN,
    LABEL_FR,
    LABEL_DE,
    LABEL_IT,
    BACK_TO_TRAVEL_BUTTON,
    BACK_TO_COVERFORYOU_BUTTON,
    BACK_TO_CEDARTREE_BUTTON,
    BACK_TO_OUTBACKER_BUTTON,
    BACK_TO_INSUREANDESCAPE_BUTTON,
    REQUEST_VIDEO_CONSULTATION,
    REQUEST_PHONE_CALL,
    APPOINTMENTS,
    TELECONSULTATION,
    REQUEST_VIDEO_CONSULTATION_BR,
    REQUEST_PHONE_CALL_BR,
    APPOINTMENTS_BR,
    TELECONSULTATION_BR,
  } = locale;

  const languages = [
    { label: LABEL_ES, value: es_ES, idiom: 'es_ES' },
    { label: LABEL_EN, value: en_US, idiom: 'en_US' },
    { label: LABEL_PT, value: pt_BR, idiom: 'pt_BR' },
    { label: LABEL_FR, value: fr_FR, idiom: 'fr_FR' },
    { label: LABEL_DE, value: de_DE, idiom: 'de_DE' },
    { label: LABEL_IT, value: it_IT, idiom: 'it_IT' },
  ];

  const handleChangeIdiom = async (e) => {
    try {
      setIsLoading(true);
      setIdiom(e.target.value);
      setIsLoading(false);
      document.documentElement.lang = e.target.value.substr(0, 2);
    } catch {
      setIsLoading(false);
    }
  };

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const location = useLocation();
  const noHash = !['#appointments'].includes(location.hash);

  const isRequestVideoActive = () => {
    const firstVerify = ['/patient-selection?type=video'].includes(
      location.pathname + location.search
    );
    const secondVerify = ['/lets-start?type=video'].includes(
      location.pathname + location.search
    );
    if (secondVerify) return secondVerify && noHash;
    if (!firstVerify) {
      const arr = location.pathname.split('/');
      return arr.includes('request') && arr.includes('video');
    }
    return firstVerify && noHash;
  };

  const isRequestPhoneActive = () => {
    const firstVerify = ['/patient-selection?type=phone'].includes(
      location.pathname + location.search
    );
    const secondVerify = ['/lets-start?type=phone'].includes(
      location.pathname + location.search
    );
    if (secondVerify) return secondVerify && noHash;
    if (!firstVerify) {
      const arr = location.pathname.split('/');
      return arr.includes('request') && arr.includes('phone');
    }
    return firstVerify && noHash;
  };

  const isAppointmentsActive = () => {
    return ['#appointments'].includes(location.hash);
  };

  const isHomeActive = () => {
    return (
      ['/', '/login-callback'].includes(location.pathname) &&
      !['#appointments'].includes(location.hash)
    );
  };

  const getFlag = () => {
    return (
      <PublicIcon sx={{ color: 'dimgray', width: '1em', height: '1em' }} />
    );
  };

  let sourceCS = false;
  const params = new URLSearchParams(window.location.search);
  if (
    window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC ||
    window.location.hostname === import.meta.env.VITE_DOMAIN_TRIPUK ||
    window.location.hostname === import.meta.env.VITE_DOMAIN_US ||
    window.location.hostname === import.meta.env.VITE_DOMAIN_US_CHUBB ||
    window.location.hostname === import.meta.env.VITE_DOMAIN_US_METLIFE ||
    window.location.hostname === import.meta.env.VITE_DOMAIN_USTI ||
    window.location.hostname === import.meta.env.VITE_DOMAIN_WWIIS
  ) {
    const source = params.get('source');
    const utmSource = params.get('utm_source');
    if (source && source.toLowerCase() === 'cs') {
      sourceCS = true;
      setCookie('source', 'CS');
    } else if (source && source.toLowerCase() !== 'cs') {
      sourceCS = false;
      deleteCookie('source');
    } else if (
      getCookie('source') &&
      getCookie('source').toLowerCase() === 'cs'
    ) {
      sourceCS = true;
    } else if (utmSource && utmSource.toLowerCase() === 'mtc') {
      setCookie('source', 'MTC');
    } else if (utmSource && utmSource.toLowerCase() !== 'mtc') {
      deleteCookie('source');
    }
  }

  return (
    <Container data-testid="header">
      <Grid container sx={{ width: '100%' }}>
        <Loading active={isLoading} />

        {/* -- MOBILE: Back button row -- */}
        {!isDesktop && (
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid
              sx={{
                backgroundColor: '#FAFAFA',
                height: '2.5em',
                mx: '1em',
              }}
            >
              {window.location.hostname === import.meta.env.VITE_DOMAIN_TRIPUK
                ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                : window.location.hostname ===
                    import.meta.env.VITE_DOMAIN_US_CHUBB
                  ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                  : window.location.hostname ===
                      import.meta.env.VITE_DOMAIN_US_METLIFE
                    ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                    : window.location.hostname ===
                        import.meta.env.VITE_DOMAIN_USTI
                      ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                      : window.location.hostname ===
                          import.meta.env.VITE_DOMAIN_WWIIS
                        ? BackToTravelButton(
                            '',
                            BACK_TO_COVERFORYOU_BUTTON,
                            BACK_TO_CEDARTREE_BUTTON,
                            BACK_TO_OUTBACKER_BUTTON,
                            BACK_TO_INSUREANDESCAPE_BUTTON
                          )
                        : sourceCS
                          ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                          : ''}
            </Grid>
          </Grid>
        )}

        {/* -- DESKTOP -- */}
        {isDesktop && (
          <>
            {/* Row 1: Back button + Language selector */}
            <Grid
              container
              alignItems="center"
              justifyContent="flex-end"
              sx={{ width: '100%' }}
            >
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Grid
                  sx={{
                    backgroundColor: '#FAFAFA',
                    height: '2.5em',
                    mx: '1em',
                  }}
                >
                  {window.location.hostname ===
                  import.meta.env.VITE_DOMAIN_TRIPUK
                    ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                    : window.location.hostname ===
                        import.meta.env.VITE_DOMAIN_US_CHUBB
                      ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                      : window.location.hostname ===
                          import.meta.env.VITE_DOMAIN_US_METLIFE
                        ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                        : window.location.hostname ===
                            import.meta.env.VITE_DOMAIN_USTI
                          ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                          : window.location.hostname ===
                              import.meta.env.VITE_DOMAIN_WWIIS
                            ? BackToTravelButton(
                                '',
                                BACK_TO_COVERFORYOU_BUTTON,
                                BACK_TO_CEDARTREE_BUTTON,
                                BACK_TO_OUTBACKER_BUTTON,
                                BACK_TO_INSUREANDESCAPE_BUTTON
                              )
                            : sourceCS
                              ? BackToTravelButton(BACK_TO_TRAVEL_BUTTON)
                              : ''}
                </Grid>
                <Grid
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    fontWeight: 600,
                    mx: '1em',
                  }}
                >
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    flexItem
                    sx={dividerLanguageSx}
                  />
                  <Grid
                    sx={{
                      mx: '1em',
                      height: '25px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {getFlag()}
                  </Grid>
                  <Grid sx={{ width: '7.5em', mr: '1em' }}>
                    {SelectLanguage(idiom, handleChangeIdiom, languages)}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Row 2: Logo (md=1) + Nav Buttons (md=11) with shadow */}
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              sx={{
                boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                backgroundColor: 'white',
                width: '100%',
              }}
            >
              <Grid size={{ md: 1 }}>{PageIcon()}</Grid>
              <Grid
                size={{ md: 11 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: '4em',
                }}
              >
                {userData?.email_verified && (
                  <Grid>
                    <TeleconsultationButton
                      isHomeActive={isHomeActive}
                      label={TELECONSULTATION_BR}
                    />
                    <RequestVideoButton
                      idiom={idiom}
                      isRequestVideoActive={isRequestVideoActive}
                      label={REQUEST_VIDEO_CONSULTATION_BR}
                    />
                    <RequestPhoneButton
                      idiom={idiom}
                      isRequestPhoneActive={isRequestPhoneActive}
                      label={REQUEST_PHONE_CALL_BR}
                    />
                    <AppointmentsButton
                      isAppointmentsActive={isAppointmentsActive}
                      label={APPOINTMENTS_BR}
                    />
                  </Grid>
                )}
                {userData?.email_verified && (
                  <Grid sx={{ mx: '1em' }}>
                    <UserMenu />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* -- MOBILE: Header bar (Logo + Menu icon) -- */}
        {!isDesktop && (
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            sx={{
              boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
              backgroundColor: 'white',
              width: '100%',
            }}
          >
            <Grid>{PageIcon()}</Grid>
            <Grid>
              <Tooltip title="Menu">
                <IconButton onClick={handleOpenDrawer} aria-label="Menu">
                  <MenuIcon sx={{ color: '#00008F' }} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* -- MOBILE: Drawer -- */}
      {!isDesktop && (
        <SwipeableDrawer
          anchor="right"
          open={openDrawer}
          onClose={handleCloseDrawer}
          onOpen={handleOpenDrawer}
        >
          <Grid container sx={{ width: '100%', height: '100%' }}>
            <Grid container sx={{ width: '100%', height: '100%' }}>
              <Grid sx={{ width: '100%', height: '55vh', minHeight: '21em' }}>
                {/* Drawer header: logo + close */}
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: '100%', height: '4em' }}
                >
                  <Grid>{PageIcon()}</Grid>
                  <Grid sx={{ height: '100%', width: '4em' }}>
                    <Grid
                      container
                      justifyContent="center"
                      sx={{ height: '100%', width: '4em' }}
                    >
                      <Grid>
                        <IconButton
                          onClick={handleCloseDrawer}
                          sx={{ height: '100%' }}
                        >
                          <CloseRoundedIcon sx={{ color: '#00008F' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Divider sx={mobileDividerSx} flexItem />
                {userData?.email_verified && (
                  <>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      sx={{ my: '1em' }}
                    >
                      <Grid>
                        <UserMenu mobile />
                      </Grid>
                    </Grid>
                    <Divider sx={mobileDividerSx} flexItem />
                    <Grid sx={{ width: '100%' }}>
                      <Grid>
                        <TeleconsultationButton
                          isHomeActive={isHomeActive}
                          label={TELECONSULTATION}
                          mobile={true}
                          handleCloseDrawer={handleCloseDrawer}
                        />
                      </Grid>
                      <Divider sx={mobileDividerSx} flexItem />
                      <Grid>
                        <RequestVideoButton
                          idiom={idiom}
                          isRequestVideoActive={isRequestVideoActive}
                          label={REQUEST_VIDEO_CONSULTATION}
                          mobile={true}
                          handleCloseDrawer={handleCloseDrawer}
                        />
                      </Grid>
                      <Divider sx={mobileDividerSx} flexItem />
                      <Grid>
                        <RequestPhoneButton
                          idiom={idiom}
                          isRequestPhoneActive={isRequestPhoneActive}
                          label={REQUEST_PHONE_CALL}
                          mobile={true}
                          handleCloseDrawer={handleCloseDrawer}
                        />
                      </Grid>
                      <Divider sx={mobileDividerSx} flexItem />
                      <Grid>
                        <AppointmentsButton
                          isAppointmentsActive={isAppointmentsActive}
                          label={APPOINTMENTS}
                          mobile={true}
                          handleCloseDrawer={handleCloseDrawer}
                        />
                      </Grid>
                      <Divider sx={mobileDividerSx} flexItem />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid sx={{ width: '100%', height: '15vh', minHeight: '6em' }}>
                <Divider sx={mobileDividerSx} flexItem />
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  sx={{ height: '48%', width: '100%' }}
                >
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: '100%' }}
                  >
                    <Grid sx={{ mr: '1em' }}>{getFlag()}</Grid>
                    <Grid sx={{ width: '80%' }}>
                      {SelectLanguage(idiom, handleChangeIdiom, languages)}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      )}
    </Container>
  );
}

function PageIcon() {
  return (
    <a href="/" aria-label="Doctor Please">
      <img
        src={logo}
        height="57px"
        alt=""
        style={{ marginLeft: '1em', marginRight: '1em' }}
        title="Speak to an international Physician for medical support.Prescription will depend on the clinical need and local regulations."
      />
    </a>
  );
}

function AppointmentsButton({
  isAppointmentsActive,
  label: APPOINTMENTS,
  mobile = false,
  handleCloseDrawer = () => {},
}) {
  const navigate = useNavigate();
  return (
    <NavButton
      sx={{
        ...drawerButtonSx,
        ...(isAppointmentsActive() ? activeSx : {}),
        ...(mobile ? mobileHeaderButtonSx : headerButtonSx),
      }}
      onClick={() => {
        handleCloseDrawer();
        navigate('/#appointments');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          zone: 'header',
          element_clicked: 'appointments',
          event: 'navigation',
        });
        window.dataLayer.push({
          subsidiary_name: 'axa_partners',
          business_unit: 'customer_and_solutions',
          business_line: 'travel_solutions',
          digital_business_process: 'assistance_request',
          global_digital_asset: 'doctor_please',
          partner_id: getPartnerId(),
          partner_name: getPartnerName(),
          country: getCountryCode(),
          language: getLanguage(),
          instance_environment: getInstanceEnv(),
          user_logged: 'true',
          page_name: 'appointments list',
          appointment_type: 'video call',
          funnel_step: '3',
          step_name: 'appointments list',
          event: 'pageview',
        });
      }}
    >
      <span
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(APPOINTMENTS) }}
      ></span>
    </NavButton>
  );
}

function RequestPhoneButton({
  idiom,
  isRequestPhoneActive,
  label: REQUEST_PHONE_CALL,
  mobile = false,
  handleCloseDrawer = () => {},
}) {
  const navigate = useNavigate();
  const { userData, isLogged } = useContext(GeneralContext);
  const handleButton = async (path) => {
    if (isLogged && userData?.isRegisteredAmos) {
      navigate(`/patient-selection?type=${path}`);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'header',
        element_clicked: 'phone consultation',
        service_category: 'health',
        event: 'start_journey',
      });
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'patient selection',
        funnel_step: '1',
        step_name: 'request appointment',
        event: 'pageview',
      });
    }
    if (isLogged && userData?.isRegisteredAmos === false) {
      navigate(`/lets-start?type=${path}`);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'header',
        element_clicked: 'call back request',
        service_category: 'health',
        event: 'start_journey',
      });
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'register doctor please',
        funnel_step: '0.1',
        step_name: 'register doctor please',
        event: 'pageview',
      });
    }
    if (!isLogged) {
      navigate(`/login?lang=${idiom.substr(0, 2)}`);
    }
  };

  return (
    <NavButton
      sx={{
        ...drawerButtonSx,
        ...(isRequestPhoneActive() ? activeSx : {}),
        ...(mobile ? mobileHeaderButtonSx : headerButtonSx),
      }}
      onClick={() => {
        handleButton('phone');
        handleCloseDrawer();
      }}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(REQUEST_PHONE_CALL),
        }}
      ></span>
    </NavButton>
  );
}

function RequestVideoButton({
  idiom,
  isRequestVideoActive,
  label: REQUEST_VIDEO_CONSULTATION,
  mobile = false,
  handleCloseDrawer = () => {},
}) {
  const navigate = useNavigate();
  const { userData, isLogged } = useContext(GeneralContext);
  const handleButton = async (path) => {
    if (isLogged && userData?.isRegisteredAmos) {
      navigate(`/patient-selection?type=${path}`);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'header',
        element_clicked: 'video consultation',
        service_category: 'health',
        event: 'start_journey',
      });
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'patient selection',
        funnel_step: '1',
        step_name: 'request appointment',
        event: 'pageview',
      });
    }
    if (isLogged && userData?.isRegisteredAmos === false) {
      navigate(`/lets-start?type=${path}`);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        zone: 'header',
        element_clicked: 'video consultation',
        service_category: 'health',
        event: 'start_journey',
      });
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'register doctor please',
        funnel_step: '0.1',
        step_name: 'register doctor please',
        event: 'pageview',
      });
    }
    if (!isLogged) {
      navigate(`/login?lang=${idiom.substr(0, 2)}`);
    }
  };
  return (
    <NavButton
      sx={{
        ...drawerButtonSx,
        ...(isRequestVideoActive() ? activeSx : {}),
        ...(mobile ? mobileHeaderButtonSx : headerButtonSx),
      }}
      onClick={() => {
        handleButton('video');
        handleCloseDrawer();
      }}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(REQUEST_VIDEO_CONSULTATION),
        }}
      ></span>
    </NavButton>
  );
}

function TeleconsultationButton({
  isHomeActive,
  label: TELECONSULTATION,
  mobile = false,
  handleCloseDrawer = () => {},
}) {
  const navigate = useNavigate();
  return (
    <NavButton
      sx={{
        ...drawerButtonSx,
        ...(isHomeActive() ? activeSx : {}),
        ...(mobile ? mobileHeaderButtonSx : headerButtonSx),
      }}
      onClick={() => {
        handleCloseDrawer();
        navigate('/');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          zone: 'header',
          element_clicked: 'home',
          event: 'navigation',
        });
        window.dataLayer.push({
          subsidiary_name: 'axa_partners',
          business_unit: 'customer_and_solutions',
          business_line: 'travel_solutions',
          digital_business_process: 'assistance_request',
          global_digital_asset: 'doctor_please',
          partner_id: getPartnerId(),
          partner_name: getPartnerName(),
          country: getCountryCode(),
          language: getLanguage(),
          instance_environment: getInstanceEnv(),
          user_logged: 'true',
          page_name: 'homepage',
          event: 'pageview',
        });
      }}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(TELECONSULTATION),
        }}
      ></span>
    </NavButton>
  );
}

function SelectLanguage(idiom, handleChangeIdiom, languages) {
  return (
    <FormControl fullWidth>
      <Select
        value={idiom}
        onChange={handleChangeIdiom}
        variant="standard"
        disableUnderline
        data-testid="languageSelect"
        sx={{
          fontFamily: 'Source Sans Pro',
          textTransform: 'uppercase',
          color: 'dimgray',
          fontWeight: 600,
          lineHeight: '18px',
          fontSize: '13px',
          letterSpacing: '0.2px',
        }}
        IconComponent={() => <ChevronDown />}
      >
        {languages?.map((el, key) => (
          <MenuItem value={el.value} key={key}>
            {el.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function BackToTravelButton(
  BACK_TO_TRAVEL_BUTTON,
  BACK_TO_COVERFORYOU_BUTTON,
  BACK_TO_CEDARTREE_BUTTON,
  BACK_TO_OUTBACKER_BUTTON,
  BACK_TO_INSUREANDESCAPE_BUTTON,
  handleCloseDrawer = () => {}
) {
  const idiom = getCookie('idiom');
  let URL = '';
  const urlParams = new URLSearchParams(window.location.search);
  const brand = urlParams.get('Brand');
  const policyNumber = urlParams.get('policyNumber');
  if (brand && policyNumber) {
    setCookie('policyNumber', policyNumber);
    setCookie('brand', brand);
  }
  if (window.location.hostname === import.meta.env.VITE_DOMAIN_TRIPUK) {
    URL = import.meta.env.VITE_TRIP_UK_URL;
  } else if (window.location.hostname === import.meta.env.VITE_DOMAIN_GENERIC) {
    URL = import.meta.env.VITE_MYTRIP_COMPANION_GENERIC;
  } else if (
    window.location.hostname === import.meta.env.VITE_DOMAIN_US_CHUBB
  ) {
    URL = import.meta.env.VITE_US_CHUBB_URL;
  } else if (
    window.location.hostname === import.meta.env.VITE_DOMAIN_US_METLIFE
  ) {
    URL = import.meta.env.VITE_US_METLIFE_URL;
  } else if (window.location.hostname === import.meta.env.VITE_DOMAIN_USTI) {
    URL = import.meta.env.VITE_USTI_URL;
  } else if (window.location.hostname === import.meta.env.VITE_DOMAIN_WWIIS) {
    const brandCookies = getCookie('brand');
    const policyNumbers = getCookie('policyNumber');
    if (!brand && !policyNumber && !brandCookies && !policyNumbers) {
      const arrow = document.querySelector('[name="backArrow"]');
      if (arrow) {
        arrow.style.display = 'none';
      }
    }
    if (brandCookies === 'CoverForYou') {
      URL = import.meta.env.VITE_WWIIS_URL1 + `${policyNumbers}`;
    } else if (brandCookies === 'CedarTree') {
      URL = import.meta.env.VITE_WWIIS_URL2 + `${policyNumbers}`;
    } else if (brandCookies === 'Outbacker') {
      URL = import.meta.env.VITE_WWIIS_URL3 + `${policyNumbers}`;
    } else if (brandCookies === 'InsureAndEscape') {
      URL = import.meta.env.VITE_WWIIS_URL4 + `${policyNumbers}`;
    }
  }
  const brandCookie = getCookie('brand');
  if (
    idiom != undefined &&
    idiom != null &&
    URL !== '' &&
    window.location.hostname !== import.meta.env.VITE_DOMAIN_WWIIS
  ) {
    URL += '?lng_code=' + idiom.substr(0, 2);
  }
  return (
    <Button
      sx={drawerButtonBackSx}
      variant="text"
      name="backArrow"
      startIcon={<ArrowBackIcon />}
      href={URL}
      onClick={() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          zone: 'header',
          element_clicked: 'home',
          event: 'navigation',
        });
        handleCloseDrawer();
      }}
    >
      {import.meta.env.VITE_DOMAIN_WWIIS && brandCookie === 'CoverForYou'
        ? BACK_TO_COVERFORYOU_BUTTON
        : import.meta.env.VITE_DOMAIN_WWIIS && brandCookie === 'CedarTree'
          ? BACK_TO_CEDARTREE_BUTTON
          : import.meta.env.VITE_DOMAIN_WWIIS && brandCookie === 'Outbacker'
            ? BACK_TO_OUTBACKER_BUTTON
            : import.meta.env.VITE_DOMAIN_WWIIS &&
                brandCookie === 'InsureAndEscape'
              ? BACK_TO_INSUREANDESCAPE_BUTTON
              : BACK_TO_TRAVEL_BUTTON}
    </Button>
  );
}
